import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation, Trans } from 'react-i18next';

import { t } from 'i18next';

import { useTheme } from '@mui/material/styles';
import {
    Button,
    Grid,
    Paper,
    TextField,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Autocomplete,
    Box,
    createFilterOptions,
    ToggleButtonGroup,
} from '@mui/material';

import {
    ArrowBack,
    ArrowDownward,
    ArrowForward,
    ArrowUpward,
    Cancel,
    Close,
    CopyAll,
    RotateLeft,
    RotateRight,
    Save
} from '@mui/icons-material';

import store from '../../../../store';

import {
    setStoreData,
    setIsInfoWindowVisible,
    setSelectedStoreElement,
    setUnsavedModifications,
    setIsDragEnabled,
    showProjectElements,
    addFloorPlanElement,
    updateFloorPlanElement,
    removeFloorPlanElement,
    removeRuler,
    setIsSceneUpdated,
    updateRuler
} from '../../../../store/actions/Store';
import { toggleMessageOpen } from '../../../../store/actions/Message';
import { createConfirmAction } from '../../../../store/actions/ConfirmDialog';

import apiClient from '../../../../services/api';

import { generateUniqueString, getRotatedElementWrapperSize, useDebounce } from '../../../../utils/Utils';

import StyledButton from '../../../styled/Button';
import StyledToggleButton from '../../../styled/ToggleButton';
import StyledTextField from '../../../styled/TextField';
import StyledIconButton from '../../../styled/IconButton';

import { FLOOR_ELEMENT_ID } from './layers/Elements';

const DefaultInfoWindow = (props) => {
    const theme = useTheme();

    return (
        <Paper
            ref={props.innerRef}
            sx={{ padding: 1, backgroundColor: theme.palette.background.secondary, marginBottom: '5px', border: '1px solid', borderColor: theme.palette.border.main }}
        >
            <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h3">
                        <Trans>Element Information</Trans>
                    </Typography>
                    <StyledIconButton
                        size="small"
                        color="primary"
                        edge="end"
                        onClick={props.onClose}
                    >
                        <Close />
                    </StyledIconButton>
                </Box>
            </Box>
            
            <Typography sx={{ padding: 1, paddingRight: 3, fontWeight: 'bold', wordWrap: 'break-word' }}>
                {props.t('Project')+':'}&nbsp;{props.element ? props.element.name : ''}
            </Typography>

            <Grid container sx={{ padding: 1 }}>
                <Grid item xs={4}>
                    <div>
                        {props.t('ProjectNr')}
                    </div>
                    <div>
                        {props.element ? props.element.number : ''}
                    </div>
                </Grid>
                <Grid item xs={4}>
                    <div>
                        {props.t('Width(cm)')}
                    </div>
                    <div>
                        {props.element ? Number(props.element.width).toFixed(3) : ''}
                    </div>
                </Grid>
                <Grid item xs={4}>
                    <div>
                        {props.t('Depth(cm)')}
                    </div>
                    <div>
                        {props.element ? Number(props.element.depth).toFixed(3) : ''}
                    </div>
                </Grid>
            </Grid>

            <Grid container>
                <Grid item xs={6} sx={{ padding: 1 }}>
                    <StyledButton
                        fullWidth
                        size="small"
                        color="primary"
                        variant="outlined"
                        onClick={() => { props.onViewProducts(props.element) }}
                    >
                        {props.t('View Products')}
                    </StyledButton>
                </Grid>
                <Grid item xs={6} sx={{ padding: 1 }}>
                    <StyledButton
                        fullWidth
                        size="small"
                        color="primary"
                        variant="outlined"
                        onClick={() => { props.onViewFixtures(props.element) }}
                    >
                        {props.t('View Fixtures')}
                    </StyledButton>
                </Grid>
            </Grid>

            <Grid container>
                <Grid item xs={6} sx={{padding: 1}}>
                    <StyledTextField
                        value={props.posX}
                        onChange={props.onPosXChanged}
                        label={props.t('PositionX')}
                        type='number'
                        variant='outlined'
                        size='small'
                        fullWidth
                    />
                </Grid>
                <Grid item xs={6} sx={{padding: 1}}>
                    <StyledTextField
                        value={props.posY}
                        onChange={props.onPosYChanged}
                        label={props.t('PositionY')}
                        variant='outlined'
                        type='number'
                        size='small'
                        fullWidth
                    />
                </Grid>
            </Grid>

            <Grid container justifyContent='center' marginTop='5px'>
                <Grid item xs={4} sx={{textAlign: 'center'}}>
                    <IconButton onClick={props.rotateLeft}>
                        <RotateLeft color='primary' />
                    </IconButton>
                </Grid>
                <Grid item xs={4}>
                    <StyledTextField
                        value={props.angle}
                        onChange={props.onAngleChanged}
                        label={props.t('Angle')}
                        type='number'
                        variant='outlined'
                        size='small'
                        fullWidth
                        InputProps={{ inputProps: { min: 0, max: 360 } }}
                    />
                </Grid>
                <Grid item xs={4} sx={{textAlign: 'center'}}>
                    <IconButton onClick={props.rotateRight}>
                        <RotateRight color='primary' />
                    </IconButton>
                </Grid>
            </Grid>

            <Grid container>
                <Grid item xs={6} sx={{padding: 1}}>
                    <StyledButton
                        variant="contained"
                        size="small"
                        fullWidth
                        onClick={() => {
                            props.setDialogOpen(true)
                        }}
                    >
                        {props.t('Clone')}
                    </StyledButton>
                </Grid>
                <Grid item xs={6} sx={{padding: 1}}>
                    <StyledButton
                        variant="contained"
                        size="small"
                        fullWidth
                        onClick={() => {
                            store.dispatch(createConfirmAction(props.removeElement, props.t('Are you sure?')))
                        }}
                    >
                        {props.t('Remove')}
                    </StyledButton>
                </Grid>
            </Grid>
        </Paper>
    );
}

const WallInfoWindow = (props) => {
    const theme = useTheme();

    return (
        <Paper 
            sx={{ padding: 1, backgroundColor: theme.palette.background.secondary, marginBottom: '5px', border: '1px solid', borderColor: theme.palette.border.main }}
            ref={props.innerRef}
        >
            <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h3">
                        <Trans>Element Information</Trans>
                    </Typography>
                    <StyledIconButton
                        size="small"
                        color="primary"
                        edge="end"
                        onClick={props.onClose}
                    >
                        <Close />
                    </StyledIconButton>
                </Box>
            </Box>
            <Typography sx={{fontWeight: 'bold', padding: 1}}>
                {props.t('Wall')}
            </Typography>

            <Grid container sx={{padding: 1}} spacing={1}>
                <Grid item xs={6}>
                    <StyledTextField
                        value={Number(props.width).toFixed(3)}
                        onChange={props.onWidthChanged}
                        label={props.t('Width(cm)')}
                        type='number'
                        variant='outlined'
                        size='small'
                        fullWidth
                    />
                </Grid>
                <Grid item xs={6}>
                    <StyledTextField
                        value={Number(props.depth).toFixed(3)}
                        onChange={props.onDepthChanged}
                        label={props.t('Depth(cm)')}
                        type='number'
                        variant='outlined'
                        size='small'
                        fullWidth
                    />
                </Grid>
            </Grid>

            <Button
                variant="contained"
                size='small'
                style={{backgroundColor: theme.palette.background.button, color: theme.palette.white.main}}
                fullWidth
                onClick={() => {
                    store.dispatch(createConfirmAction(props.removeElement, props.t('Are you sure?')))
                }}
            >
                {props.t('Remove')}
            </Button>
        </Paper>
    );
}

const RulerInfoWindow = (props) => {
    const theme = useTheme();

    const { storeData: sd } = props;
    const element = useSelector((state) => state.store.selectedStoreElement);

    const [rulerValue, setRulerValue] = useState(null);

    const scale = Number(sd?.floor_plan?.scale) || 1;

    const x = Number(props.element.x);
    const y = Number(props.element.y);

    const value = props.element.type === 'vertical' ? (x / scale).toFixed(2) : (y / scale).toFixed(2);
    const st = sd && sd.floor_plan ? sd.floor_plan : null;
    const stw = st ? st.width : 0;
    const std = st ? st.depth : 0;

    useEffect(() => {
        if(value) {
            setRulerValue(value);
        }
    }, [value]);

    const onRulerValueChanged = (event) => {
        const scaledValue = 100 * Number(event.target.value);
        
        if(props.element.type === 'vertical') {
            if(stw < scaledValue) {
                setAndStoreValue((stw/100).toFixed(2));

                return;
            }

            if(scaledValue < 0) {
                setAndStoreValue(0);

                return;
            }
        } else {
            if(std < scaledValue) {
                setAndStoreValue((std/100).toFixed(2));

                return;
            }

            if(scaledValue < 0) {
                setAndStoreValue(0);

                return;
            }
        }

        setAndStoreValue(event.target.value);
    }

    const setAndStoreValue = (value) => {
        setRulerValue(value);

        if(props.element.type === 'vertical') {
            store.dispatch(updateRuler({ ...element, ...{x: value * 100, y: y} }));
        } else {
            store.dispatch(updateRuler({ ...element, ...{x: x, y: value * 100} }));
        }
    }

    return (
        <Paper 
            sx={{ padding: 1, backgroundColor: theme.palette.background.secondary, marginBottom: '5px', border: '1px solid', borderColor: theme.palette.border.main }}
            ref={props.innerRef}
        >
            <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h3">
                        <Trans>Element Information</Trans>
                    </Typography>
                    <StyledIconButton
                        size="small"
                        color="primary"
                        edge="end"
                        onClick={props.onClose}
                    >
                        <Close />
                    </StyledIconButton>
                </Box>
            </Box>
            <Typography sx={{fontWeight: 'bold', padding: 1}}>
                {props.t('Ruler')}
            </Typography>

            <Grid container sx={{padding: 1}} alignItems='center'>
                <Grid item xs={6}>
                    {props.t(props.element.type) + ' (m)'}
                </Grid>
                <Grid item xs={6}>
                    <StyledTextField
                        value={rulerValue}
                        onChange={(event) => { onRulerValueChanged(event) }}
                        label={props.t('Position')}
                        type='number'
                        variant='outlined'
                        size='small'
                        fullWidth
                        readonly
                    />
                </Grid>
            </Grid>

            <Button
                variant="contained"
                size='small'
                style={{backgroundColor: theme.palette.background.button, color: theme.palette.white.main}}
                fullWidth
                onClick={() => {
                    store.dispatch(createConfirmAction(props.removeRuler, props.t('Are you sure?')))
                }}
            >
                {props.t('Remove')}
            </Button>
        </Paper>
    );
}

const FloorPlanInfoWindow = (props) => {
    const theme = useTheme();

    const storeData = props.storeData;
    const txo = storeData && storeData.dxf ? storeData.dxf : null;
    const isDXF = txo && txo.endsWith('.dxf');

    return (
        <Paper
            sx={{ padding: 1, backgroundColor: theme.palette.background.secondary, marginBottom: '5px', border: '1px solid', borderColor: theme.palette.border.main }}
            ref={props.innerRef}
        >
            <Box>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h3">
                        <Trans>Element Information</Trans>
                    </Typography>
                    <StyledIconButton
                        size="small"
                        color="primary"
                        edge="end"
                        onClick={props.onClose}
                    >
                        <Close />
                    </StyledIconButton>
                </Box>
            </Box>
            <Typography sx={{padding: 1, fontWeight: 'bold'}}>
                {props.t('Floor plan')}
            </Typography>
            <Grid container spacing={1} sx={{padding: 1}} justifyContent='center' alignItems='center'>
                <Grid item xs={6}>
                    <StyledTextField
                        value={Number(props.storeWidth).toFixed(2)}
                        onChange={(event) => { props.setStoreWidth(event.target.value) }}
                        type='number'
                        variant='outlined'
                        label={props.t('Width(m)')}
                        size='small'
                        InputProps={{inputProps: {min: 1}}}
                        fullWidth
                        onKeyDown={props.handleKeyDown}
                        disabled={isDXF}
                    />
                </Grid>
                <Grid item xs={6}>
                    <StyledTextField
                        value={Number(props.storeDepth).toFixed(2)}
                        onChange={(event) => { props.setStoreDepth(event.target.value) }}
                        type='number'
                        variant='outlined'
                        label={props.t('Depth(m)')}
                        size='small'
                        InputProps={{inputProps: {min: 1}}}
                        fullWidth
                        onKeyDown={props.handleKeyDown}
                        disabled={isDXF}
                    />
                </Grid>
            </Grid>
            <Grid
                container
                spacing={1}
                sx={{
                    padding: 1,
                    display: (props.isGridEnabled ? 'flex' : 'none')
                }}
                justifyContent='center'
                alignItems='center'
            >
                <Grid item xs={6}>
                    <StyledTextField
                        value={props.gridWidth}
                        onChange={(event) => { props.setGridWidth(event.target.value) }}
                        type='number'
                        variant='outlined'
                        label={props.t('Grid Width')}
                        size='small'
                        InputProps={{inputProps: {min: 1}}}
                        fullWidth
                        onKeyDown={props.handleKeyDown}
                    />
                </Grid>
                <Grid item xs={6}>
                    <StyledTextField
                        value={props.gridDepth}
                        onChange={(event) => { props.setGridDepth(event.target.value) }}
                        type='number'
                        variant='outlined'
                        label={props.t('Grid Depth')}
                        size='small'
                        InputProps={{inputProps: {min: 1}}}
                        fullWidth
                        onKeyDown={props.handleKeyDown}
                    />
                </Grid>
            </Grid>

            <Button
                variant="contained"
                size="small"
                fullWidth
                style={{backgroundColor: theme.palette.background.button, color: theme.palette.white.main, marginTop: '10px'}}
                onClick={props.handleUpdateFloorPlan}
                startIcon={<Save />}
            >
                {props.t('Update')}
            </Button>
        </Paper>
    );
}

const CloneDialog = (props) => {
    const theme = useTheme();

    return (
        <Dialog open={props.dialogOpen} fullWidth style={{ zIndex: 2000 }}>
            <DialogTitle style={{backgroundColor: theme.palette.background.secondary}}>
                <Typography>
                    {props.t('Clone element')}
                </Typography>
            </DialogTitle>
            <DialogContent style={{padding: '10px', minHeight: '420px', backgroundColor: theme.palette.background.secondary}}>
                <StyledTextField
                    variant="outlined"
                    size="small"
                    value={props.name}
                    name="name"
                    label="Name"
                    type="text"
                    onChange={(event) => {props.setName(event.target.value)}}
                    fullWidth
                    required
                />
                <TextField
                    variant="outlined"
                    size="small"
                    value={props.distance}
                    name="distance"
                    label="Distance"
                    type="number"
                    onChange={(event) => {props.setDistance(event.target.value)}}
                    fullWidth
                    style={{marginTop: '10px', marginBottom: '10px'}}
                />
                <Typography>{t('Select direction')}</Typography>
                <ToggleButtonGroup
                    value={props.direction}
                    exclusive
                    fullWidth
                    size="small"
                    color="primary"
                    style={{ position: 'relative' }}
                    onChange={props.handleDirectionChange}
                >
                    <StyledToggleButton
                        value="up"
                        style={{
                            position: 'absolute',
                            border: '1px solid black',
                            borderRadius: '5px',
                            top: 0,
                            width: '10%',
                            left: '45%'
                        }}
                    >
                        <ArrowUpward color='primary' />
                    </StyledToggleButton>
                    <StyledToggleButton
                        value="left"
                        style={{
                            position: 'absolute',
                            border: '1px solid black',
                            borderRadius: '5px',
                            top: '44px',
                            width: '10%',
                            left: '39.5%'
                        }}
                    >
                        <ArrowBack color='primary' />
                    </StyledToggleButton>
                    <StyledToggleButton
                        value="right"
                        style={{
                            position: 'absolute',
                            border: '1px solid black',
                            borderRadius: '5px',
                            top: '44px',
                            width: '10%',
                            left: '50.5%'
                        }}
                    >
                        <ArrowForward color='primary' />
                    </StyledToggleButton>
                    <StyledToggleButton
                        value="down"
                        style={{
                            position: 'absolute',
                            border: '1px solid black',
                            borderRadius: '5px',
                            top: '88px',
                            width: '10%',
                            left: '45%'
                        }}
                    >
                        <ArrowDownward color='primary' />
                    </StyledToggleButton>
                </ToggleButtonGroup>
            </DialogContent>
            <DialogActions style={{backgroundColor: theme.palette.background.secondary}}>
                <Button
                    variant="contained"
                    size="small"
                    style={{backgroundColor: theme.palette.background.button, color: theme.palette.white.main}}
                    onClick={props.handleClone}
                    startIcon={<CopyAll />}
                >
                    {props.t('Clone')}
                </Button>
                <Button
                    variant="contained"
                    size="small"
                    onClick={props.handleClose}
                    style={{backgroundColor: theme.palette.background.button, color: theme.palette.white.main}}
                    startIcon={<Cancel />}
                >
                    {props.t('Close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function InfoWindow() {
    const isVisible = useSelector((state) => state.store.isInfoWindowVisible);
    const element = useSelector((state) => state.store.selectedStoreElement);
    const storeData = useSelector((state) => state.store.storeData);
    //const historyStep = useSelector((state) => state.store.historyStep);
    const isGridEnabled = useSelector((state) => state.store.isGridEnabled);

    const [posX, setPosX] = useState('');
    const [posY, setPosY] = useState('');
    const [width, setWidth] = useState('');
    const [depth, setDepth] = useState('');
    const [angle, setAngle] = useState('');

    const [dialogOpen, setDialogOpen] = useState(false);

    const [name, setName] = useState('');
    const [distance, setDistance] = useState(0);
    const [direction, setDirection] = useState(null);

    const [storeWidth, setStoreWidth] = useState(0);
    const [storeDepth, setStoreDepth] = useState(0);
    const [storeScale, setStoreScale] = useState(0);
    const [storePositionX, setStorePositionX] = useState(0);
    const [storePositionY, setStorePositionY] = useState(0);
    const [gridWidth, setGridWidth] = useState(0);
    const [gridDepth, setGridDepth] = useState(0);

    const dAngle = useDebounce(angle, 500);
    const dPositionX = useDebounce(posX, 500);
    const dPositionY = useDebounce(posY, 500);
    const dWidth = useDebounce(width, 500);
    const dDepth = useDebounce(depth, 500);

    /*
    const debouncedStoreWidth = useDebounce(storeWidth, 500);
    const debouncedStoreDepth = useDebounce(storeDepth, 500);
    const debouncedScale = useDebounce(storeScale, 500);
    const debouncedStorePositionX = useDebounce(storePositionX);
    const debouncedStorePositionY = useDebounce(storePositionY);
    const debouncedGridWidth = useDebounce(gridWidth);
    const debouncedGridDepth = useDebounce(gridDepth);
    */
    
    const filter = createFilterOptions();

    const st = storeData && storeData.floor_plan ? storeData.floor_plan : null;
    const stid = st ? st.floor_plan_id : null;
    const stw = st ? st.width : 0;
    const std = st ? st.depth : 0;
    const sts = st ? st.scale : 1;

    const tx = storeData && storeData.texture ? storeData.texture : null;
    const txw = tx ? tx.width : 0;
    const txd = tx ? tx.depth : 0;
    const txpx = tx ? tx.position_x : 0;
    const txpy = tx ? tx.position_y : 0;
    const grid_width = storeData && storeData.floor_plan ? storeData.floor_plan.grid_width : null;
    const grid_depth = storeData && storeData.floor_plan ? storeData.floor_plan.grid_depth : null;

    useEffect(() => {
        if(element) {
            if(element === FLOOR_ELEMENT_ID) {
                setStoreWidth(stw / sts);
                setStoreDepth(std / sts);
                setStoreScale(sts);
                setStorePositionX(txpx);
                setStorePositionY(txpy);
                setGridWidth(grid_width);
                setGridDepth(grid_depth);
            } else {
                setName(element.name);
                setPosX(element.positionX/100);
                setPosY(element.positionY/100);
                setAngle(element.angle);
            }
        }
    }, [element]) // eslint-disable-line react-hooks/exhaustive-deps



    useEffect(() => {
        if (dAngle !== null && dAngle !== '') updateStoreData({ angle });
    }, [dAngle]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if(dPositionX !== null && dPositionX !== '') { updateStoreData({ positionX: dPositionX * sts }); }
    }, [dPositionX]) // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if(dPositionY !== null && dPositionY !== '') { updateStoreData({ positionY: dPositionY * sts }); }
    }, [dPositionY]) // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if(dWidth !== null && dWidth !== '') { updateStoreData({ width: dWidth }); }
    }, [dWidth]) // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if(dDepth !== null && dDepth !== '') { updateStoreData({ depth: dDepth }); }
    }, [dDepth]) // eslint-disable-line react-hooks/exhaustive-deps

    const {t} = useTranslation();

    store.subscribe(() => {
        const item = store.getState().store.selectedStoreElement;

        if(!item) {
            return;
        }

        setPosX(Number(item.positionX/100).toFixed(2));
        setPosY(Number(item.positionY/100).toFixed(2));
        setWidth(Number(item.width).toFixed(2));
        setDepth(Number(item.depth).toFixed(2));
        setAngle(Number(item.angle));
    });

    const onPosXChanged = (event) => { setPosX(event.target.value) }
    const onPosYChanged = (event) => { setPosY(event.target.value) }
    const onWidthChanged = (event) => { setWidth(event.target.value) }
    const onDepthChanged = (event) => { setDepth(event.target.value) }
    const onAngleChanged = (event) => { setAngle(event.target.value) }

    const onPositionChanged = (event) => {
        const rulersClone = storeData.rulers;

        rulersClone.forEach((item) => {
            if(
                (!isNaN(parseInt(element.id)) && item.id === element.id) ||
                (isNaN(parseInt(element.id)) && item.generated_id === element.generated_id)
            ) {
                if(element.type === 'vertical') {
                    item.x = event.target.value * sts;
                } else {
                    item.y = event.target.value * sts;
                }
            }
        })

        storeData.rulers = rulersClone;

        store.dispatch(setStoreData(storeData));
        // store.dispatch(setIsSceneUpdated(true));
    }

    // why is it here?
    const updateStoreData = (update) => {
        if(!storeData || !element) return;
        if(typeof element === 'string') return;
        const updated = { ...element, ...update };

        const value = Object.values(update)[0];

        if(!value || value == 'NaN') return;

        store.dispatch(updateFloorPlanElement(updated));
        store.dispatch(setSelectedStoreElement(updated));
        store.dispatch(setUnsavedModifications(true));
    }

    const onClose = () => {
        store.dispatch(setSelectedStoreElement(null));
        store.dispatch(setIsInfoWindowVisible(false));
        // store.dispatch(setIsSceneUpdated(true));
    }
    
    const removeElement = () => {
        if(!element || !storeData || storeData.floor_plan_elements.length === 0) {
            return;
        }

        store.dispatch(removeFloorPlanElement(element));
        store.dispatch(setSelectedStoreElement(null));
    }

    const removeRulerHandler = () => {
        if (!element || !storeData || storeData.rulers.length === 0) {
            return;
        }

        store.dispatch(removeRuler(element));
        // store.dispatch(setIsSceneUpdated(true));
        store.dispatch(setSelectedStoreElement(null));
        store.dispatch(setIsInfoWindowVisible(false));
        store.dispatch(setUnsavedModifications(true));
    };

    const rotateLeft = () => { rotate('left') }

    const rotateRight = () => { rotate('right') }

    const rotate = (type) => {
        if(!element || !storeData || storeData.floor_plan_elements.length === 0) {
            return;
        }

        const clone = storeData;

        let angle;

        clone.floor_plan_elements.forEach((item) => {
            if(
                (parseInt(item.id) !== 0 && item.id === element.id) ||
                (element.generated_id !== undefined && item.generated_id === element.generated_id)
            ) {
                if(type === 'left') {
                    if(item.angle <= 90) {
                        item.angle = angle = 0;
                    } else {
                        item.angle = angle = Number(item.angle) - 90;
                    }
                } else {
                    if(item.angle >= 270) {
                        item.angle = angle = 360;
                    } else {
                        item.angle = angle = Number(item.angle) + 90;
                    }
                }
            }
        });

        clone.floor_plan_elements_3D.forEach((item) => {
            if(element.project_id == item.project_id) {
                item.positions.forEach((position) => {
                    if(position.positionX == element.positionX && position.positionY == element.positionY) {
                        position.angle = angle;
                    }
                })
            }
        });

        store.dispatch(setStoreData(clone));
        // store.dispatch(setIsSceneUpdated(true));
    }

    const handleClose = () => {
        setDialogOpen(false);
    }

    const handleDirectionChange = (event, newValue) => {
        setDirection(newValue);
    }

    const handleClone = () => {
        if(!name || !element) {
            store.dispatch(toggleMessageOpen(t('Required parameters are missing!'), 'error'));
            return;
        }

        const clone = {
            id: 0,
            generated_id: generateUniqueString(),
            floor_plan_id: element.floor_plan_id,
            project_id: element.project_id,
            width: element.width,
            height: element.height,
            depth: element.depth,
            type: element.type,
            number: element.number,
            angle: element.angle,
            positionX: getPosition('X'),
            positionY: getPosition('Y'),
            positionZ: 0,
            is_wall: element.is_wall,
            name: name,
            gltf: element.gltf
        };
        
        store.dispatch(addFloorPlanElement(clone));

        setDialogOpen(false);
    }

    const getPosition = (type) => {
        const wrapperDimensions = getRotatedElementWrapperSize(element);
        const width = wrapperDimensions.width;
        const depth = wrapperDimensions.depth;

        switch(direction) {
            case 'up': return (type === 'X' ? element.positionX : Number(element.positionY) - (Number(distance) + Number(depth) + 4))
            case 'left': return (type === 'X' ? Number(element.positionX) - (Number(distance) + Number(width) + 4) : element.positionY)
            case 'right': return (type === 'X' ? Number(element.positionX) + (Number(distance) + Number(width) + 4) : element.positionY)
            case 'down': return (type === 'X' ? element.positionX : Number(element.positionY) + (Number(distance) + Number(depth) + 4))
            default: return 0;
        }
    }

    const handleKeyDown = (event) => {
        if(event.key === 'Enter') {
            handleUpdateFloorPlan();
        }
    }

    const handleUpdateFloorPlan = () => {
        apiClient.post('/api/easy-floor/update-floor', getFloorPlanData()).then(response => {
            if(!response.data.success) {
                store.dispatch(toggleMessageOpen(response.data.message, "error"));
            } else {
                updateFloorPlanCallback(response);
            }
        })
    }

    const getFloorPlanData = () => {
        return {
            id: stid,
            width: storeWidth,
            depth: storeDepth,
            scale: storeScale,
            positionX: storePositionX,
            positionY: storePositionY,
            gridWidth: gridWidth,
            gridDepth: gridDepth
        }
    }

    const updateFloorPlanCallback = (response) => {
        const data = response.data;

        data.floor_plan.width = response.data.floor_plan.width * response.data.floor_plan.scale;
        data.floor_plan.depth = response.data.floor_plan.depth * response.data.floor_plan.scale;
        data.texture.width = response.data.texture.width * response.data.floor_plan.scale / 10;
        data.texture.depth = response.data.texture.height * response.data.floor_plan.scale / 10;
        data.floor_plan_elements = storeData.floor_plan_elements;
        data.floor_plan_elements_3D = storeData.floor_plan_elements_3D;
        data.rulers = storeData.rulers;
        
        store.dispatch(setStoreData(data));
        store.dispatch(setIsDragEnabled(true));
        //store.dispatch(setIsInfoWindowVisible(false));
        store.dispatch(setIsSceneUpdated(true));
        store.dispatch(setUnsavedModifications(false));

        store.dispatch(toggleMessageOpen(t('Floor plan data updated successfully'), "success"));
    }

    const getPositionMaxValue = (type) => {
        if(type === 'x') {
            return ((stw) - (txw || 0)) / sts;
        } else {
            return ((std) - (txd || 0)) / sts;
        }
    }

    const renderData = () => {
        if(!element) {
            return('');
        }

        if(element === FLOOR_ELEMENT_ID) {
            return <FloorPlanInfoWindow
                storeData={storeData}
                storeWidth={storeWidth}
                storeDepth={storeDepth}
                storeScale={storeScale}
                storePositionX={storePositionX}
                storePositionY={storePositionY}
                gridWidth={gridWidth}
                gridDepth={gridDepth}
                isVisible={isVisible}
                isGridEnabled={isGridEnabled}
                t={t}
                setStoreWidth={setStoreWidth}
                setStoreDepth={setStoreDepth}
                setStoreScale={setStoreScale}
                setStorePositionX={setStorePositionX}
                setStorePositionY={setStorePositionY}
                setGridWidth={setGridWidth}
                setGridDepth={setGridDepth}
                handleUpdateFloorPlan={handleUpdateFloorPlan}
                getPositionMaxValue={getPositionMaxValue}
                onClose={onClose}
                handleKeyDown={handleKeyDown}
            />
        }

        if(element.is_wall === undefined) {
            return <RulerInfoWindow
                key="ruler"
                element={element}
                isVisible={isVisible}
                storeData={storeData}
                t={t}
                onPositionChanged={onPositionChanged}
                removeRuler={removeRulerHandler}
                onClose={onClose}
            />
        }

        if(parseInt(element.is_wall) === 1) {
            return <WallInfoWindow
                key="wall"
                width={width}
                depth={depth}
                isVisible={isVisible}
                t={t}
                onWidthChanged={onWidthChanged}
                onDepthChanged={onDepthChanged}
                removeElement={removeElement}
                onClose={onClose}
            />
        }

        return (
            <DefaultInfoWindow
                key="default"
                element={element}
                posX={posX}
                posY={posY}
                isVisible={isVisible}
                angle={angle}
                t={t}
                filter={filter}
                onPosXChanged={onPosXChanged}
                onPosYChanged={onPosYChanged}
                onAngleChanged={onAngleChanged}
                rotateLeft={rotateLeft}
                rotateRight={rotateRight}
                removeElement={removeElement}
                onClose={onClose}
                setDialogOpen={setDialogOpen}
                onViewProducts={() => store.dispatch(showProjectElements('products'))}
                onViewFixtures={() => store.dispatch(showProjectElements('fixtures'))}
            />
        )
    }

    return (
        <Fragment>
            <CloneDialog
                dialogOpen={dialogOpen}
                element={element}
                name={name}
                distance={distance}
                direction={direction}
                setName={setName}
                setDistance={setDistance}
                setDirection={setDirection}
                handleClose={handleClose}
                handleClone={handleClone}
                handleDirectionChange={handleDirectionChange}
                filter={filter}
                t={t}
            />
            {renderData()}
        </Fragment>
    )
}
